import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import cn from 'classnames'

import { BP_LANDING_NARROW, upTo } from '@/utils/breakpoints'
import { useMediaQuery } from '@/utils/hooks/useMediaQuery'

import { barEnd, COUNT_UP, NARROW, PICKS, WIDE } from './heroPickData'
import styles from './HeroPick.module.scss'

/**
 * The hero: a crowd, five people picked out of it, and the card that says why one of them is here.
 *
 * It reveals ONCE and then holds. A looping hero re-earns attention it already had, and this one is
 * making a claim the reader is meant to check rather than watch: every green dot stays pointable, and
 * the card follows whichever was pointed at last. That is why the choice lives in state instead of
 * `:hover` — a hover state cannot outlive the pointer, and the answer has to stay on screen.
 *
 * The narrow layout is a different GEOMETRY, not a different stylesheet — a 15px quote does not fit
 * 324px on one line — so it is chosen here rather than by hiding a second copy of the drawing.
 */
export const HeroPick = () => {
  const { t } = useTranslation()
  const narrow = useMediaQuery(upTo(BP_LANDING_NARROW))
  const layout = narrow ? NARROW : WIDE
  const [picked, setPicked] = useState(1)

  return (
    <svg
      className={styles.figure}
      viewBox={layout.viewBox}
      data-pick={picked}
      role="img"
      aria-label={t('landing.hero.pick.title')}
      aria-describedby="heroPickDesc"
    >
      {/* No <title> element on purpose: browsers render it as a hover tooltip over the whole figure.
          The accessible name lives in aria-label instead; <desc> never tooltips. */}
      <desc id="heroPickDesc">{t('landing.hero.pick.desc')}</desc>

      {/* The person, drawn ONCE and stamped by <use>. Head plus a big lower circle whose top edge is
          the shoulder line, both clipped to the badge — the construction every avatar glyph uses,
          and the only one that renders identically in every browser at 19px. It is a silhouette on
          top of the existing dot rather than a replacement for it: the dot's radius drives the rings,
          the connectors and the hit areas, so replacing it would move three other things. */}
      <defs>
        <clipPath id="heroPersonClip">
          <circle cx="12" cy="12" r="12" />
        </clipPath>
        <symbol id="heroPerson" viewBox="0 0 24 24">
          <g clipPath="url(#heroPersonClip)">
            <circle cx="12" cy="9" r="3.7" />
            <circle cx="12" cy="21" r="7.6" />
          </g>
        </symbol>
      </defs>

      {/* The crowd steps back once and stays back: it is the scale, and it never leaves. */}
      <g className={styles.crowd}>
        {layout.crowd.map(([cx, cy], index) => (
          <circle
            key={`${cx}-${cy}`}
            className={styles.dot}
            cx={cx}
            cy={cy}
            r={layout.dotRadius}
            style={{ animationDelay: `${((index % 12) * 0.05).toFixed(2)}s` }}
          />
        ))}
        {layout.crowd.map(([cx, cy], index) => (
          <use
            key={`crowd-person-${cx}-${cy}`}
            href="#heroPerson"
            className={styles.dotPerson}
            x={cx - layout.dotRadius}
            y={cy - layout.dotRadius}
            width={layout.dotRadius * 2}
            height={layout.dotRadius * 2}
            style={{ animationDelay: `${((index % 12) * 0.05).toFixed(2)}s` }}
          />
        ))}
      </g>

      {/* One ring each, on the beat its dot turns green. */}
      {layout.seats.map(([cx, cy], index) => (
        <circle
          key={`ring-${cx}-${cy}`}
          className={styles.ring}
          cx={cx}
          cy={cy}
          r={layout.dotRadius}
          style={{ animationDelay: `${(index * 0.35).toFixed(2)}s` }}
        />
      ))}

      {layout.seats.map(([cx, cy], index) => (
        <circle
          key={`pick-${cx}-${cy}`}
          className={cn(styles.pick, { [styles.pickOn as string]: picked === index + 1 })}
          cx={cx}
          cy={cy}
          r={picked === index + 1 ? layout.pickedRadius : layout.pickRadius}
          style={{ animationDelay: `${(index * 0.35).toFixed(2)}s` }}
        />
      ))}

      {/* Sized off pickRadius and left there: the chosen dot grows to pickedRadius with an eased
          transition, and a silhouette that snapped between the two sizes popped on every hover. */}
      {layout.seats.map(([cx, cy], index) => (
        <use
          key={`pick-person-${cx}-${cy}`}
          href="#heroPerson"
          className={styles.pickPerson}
          x={cx - layout.pickRadius}
          y={cy - layout.pickRadius}
          width={layout.pickRadius * 2}
          height={layout.pickRadius * 2}
          style={{ animationDelay: `${(index * 0.35).toFixed(2)}s` }}
        />
      ))}

      {/* Every connector is drawn and `data-pick` decides which one is up, exactly as the card's
          requests work. Mounting only the picked one meant each switch created a fresh element, which
          restarts the reveal animation from zero — so the line arrived over a second late, or not at
          all if the pointer moved on first. */}
      {layout.seats.map(([cx, cy], index) => (
        <g key={`conn-${cx}-${cy}`} className={cn(styles.connector, styles[`connector-${index + 1}`])}>
          <line x1={cx} y1={cy + layout.dotRadius + 5.1} x2={cx} y2={layout.connectorY} />
        </g>
      ))}

      <g className={styles.card}>
        <rect
          className={styles.cardBg}
          x={layout.card.x}
          y={layout.card.y}
          width={layout.card.width}
          height={layout.card.height}
          rx={16}
        />
        <line className={styles.barTrack} x1={layout.bar.x1} y1={layout.bar.y} x2={layout.bar.x2} y2={layout.bar.y} />
        <text
          className={styles.scoreLabel}
          x={layout.scoreAnchor.x + (narrow ? 10 : 0)}
          y={layout.scoreAnchor.labelY}
          textAnchor={narrow ? 'start' : 'end'}
        >
          {t('landing.hero.pick.scoreLabel')}
        </text>

        {/* The score ticks up once as part of the reveal, then the real number holds. */}
        {COUNT_UP.map((value, index) => (
          <text
            key={value}
            className={cn(styles.tick, styles[`tick-${index + 1}`])}
            x={layout.scoreAnchor.x}
            y={layout.scoreAnchor.y}
            textAnchor={layout.scoreAnchor.textAnchor}
          >
            {value}%
          </text>
        ))}

        {PICKS.map((pick, index) => {
          const label = t(`landing.hero.pick.requests.${pick.key}.tag`)
          const tagWidth = Math.round(label.length * layout.tagCharWidth + layout.tagPadding)
          const isFirst = index === 0
          return (
            <g key={pick.key} className={cn(styles.request, styles[`request-${index + 1}`])}>
              <text className={styles.eyebrow} x={layout.tag.x} y={layout.eyebrowY}>
                {t(`landing.hero.pick.requests.${pick.key}.who`)}
              </text>

              {layout.quoteLines.map((y, line) => (
                <text key={y} className={styles.quote} x={layout.tag.x} y={y}>
                  {t(`landing.hero.pick.requests.${pick.key}.quote${line + 1}`, { defaultValue: '' })}
                </text>
              ))}

              <rect
                className={styles.tag}
                x={layout.tag.x}
                y={layout.tag.y}
                width={tagWidth}
                height={layout.tag.height}
                rx={8}
              />
              <text
                className={styles.tagText}
                x={layout.tag.x + tagWidth / 2}
                y={layout.tag.y + layout.tag.height - 5}
                textAnchor="middle"
              >
                {label}
              </text>
              <text className={styles.meta} x={layout.tag.x + tagWidth + 12} y={layout.metaY}>
                {t('landing.hero.pick.wroteIt', { count: pick.days })}
              </text>

              <text
                className={styles.score}
                x={layout.scoreAnchor.x}
                y={layout.scoreAnchor.y}
                textAnchor={layout.scoreAnchor.textAnchor}
              >
                {pick.score}%
              </text>
              {/* Only the first card's bar draws itself: the others are simply already at length. */}
              <line
                className={cn(styles.barFill, { [styles.barStatic as string]: !isFirst })}
                x1={layout.bar.x1}
                y1={layout.bar.y}
                x2={barEnd(layout, pick.score)}
                y2={layout.bar.y}
                pathLength={100}
              />
            </g>
          )
        })}
      </g>

      {/* Hit areas last, so they sit above everything and win the pointer. */}
      {layout.seats.map(([cx, cy], index) => (
        <circle
          key={`hit-${cx}-${cy}`}
          className={styles.hit}
          cx={cx}
          cy={cy}
          r={layout.hitRadius}
          onPointerEnter={() => setPicked(index + 1)}
          onPointerDown={() => setPicked(index + 1)}
        />
      ))}
    </svg>
  )
}
